import { TFunction } from 'i18next'
import dayjs from 'dayjs'
import EditIcons from '@/icons/EditIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import {
    createColumns,
    createInternalLinkFormatter,
    DataTableAction,
    DataTableColumn,
    FilterItemConfig,
} from '@/components/ui/data-table-customized'
import { Holiday } from '../types/response'
import { HOLIDAY_OPTIONS } from '../types/utils'

// ============================================================================
// Columns
// ============================================================================

export const getHolidayListColumns = (
    t: TFunction
): DataTableColumn<Holiday>[] =>
    createColumns<Holiday>([
        {
            key: 'holiday_id',
            title: 'ID',
            width: 80,
            sortable: true,
            pinned: 'left',
            formatter: createInternalLinkFormatter({
                pathKey: 'workdayHolidayDetail',
                pathParams: (value) => ({ id: value }),
            }),
        },
        {
            key: 'holiday_date',
            title: t('holiday-date'),
            width: 150,
            sortable: true,
            render: (value) => dayjs(value).format('DD/MM/YYYY'),
        },
        {
            key: 'holiday_name',
            title: t('holiday-name'),
            width: 250,
            render: (value) => {
                const option = HOLIDAY_OPTIONS(t).find((o) => o.value === value)
                return option ? option.label : t(value || '')
            },
        },
        {
            key: 'holiday_created_at',
            title: t('created-at'),
            width: 150,
            render: (value) => dayjs(value).format('DD/MM/YYYY'),
        },
        {
            key: 'holiday_updated_at',
            title: t('updated-at'),
            width: 150,
            render: (value) => dayjs(value).format('DD/MM/YYYY'),
        },
    ])

// ============================================================================
// Actions
// ============================================================================

interface ActionsParams {
    t: TFunction
    navigate: (path: string) => void
    onDelete: (row: Holiday) => void
}

export const getHolidayActions = ({
    t,
    navigate,
    onDelete,
}: ActionsParams): DataTableAction<Holiday>[] => [
    {
        key: 'edit',
        label: t('edit'),
        icon: <EditIcons className="w-4 h-4" />,
        variant: 'info',
        onClick: (row) => navigate(`/workdays/holiday/${row?.holiday_id}`),
    },
    {
        key: 'delete',
        label: t('delete'),
        icon: <DeleteOutlineIcons className="w-4 h-4" />,
        variant: 'danger',
        onClick: onDelete,
    },
]

// ============================================================================
// Filter Items
// ============================================================================

export const getHolidayFilterItems = (t: TFunction): FilterItemConfig[] => {
    const currentYear = new Date().getFullYear()
    const years = Array.from(
        { length: currentYear - 2000 + 1 },
        (_, index) => currentYear - index
    )
    const months = Array.from({ length: 12 }, (_, index) => index + 1)

    return [
        {
            name: 'year',
            type: 'select',
            label: t('year'),
            placeholder: t('year-placeholder'),
            toggleKey: 'showYearFilter',
            options: years.map((year) => ({
                label: `${t('year')} ${year}`,
                value: year,
            })),
        },
        {
            name: 'month',
            type: 'select',
            label: t('month'),
            placeholder: t('month-placeholder'),
            toggleKey: 'showMonthFilter',
            options: months.map((month) => ({
                label: `${t('month')} ${month}`,
                value: month,
            })),
        },
    ]
}

// ============================================================================
// Filter Toggles (Settings panel)
// ============================================================================

export const getHolidayFilterToggles = (t: TFunction) => [
    {
        key: 'showYearFilter',
        label: t('year'),
        type: 'visibility' as const,
        defaultValue: true,
    },
    {
        key: 'showMonthFilter',
        label: t('month'),
        type: 'visibility' as const,
        defaultValue: true,
    },
]
